import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../services/api';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface Booking {
  id: string;
  service_type: string;
  status: string;
  amount: number;
  created_at: string;
  booking_details: any;
}

export default function UserDashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'bookings' | 'tickets' | 'failed'>('overview');

  useEffect(() => {
    fetchBookings();
    const query = new URLSearchParams(location.search);
    const tab = query.get('tab');
    if (tab === 'tickets' || tab === 'bookings' || tab === 'failed' || tab === 'overview') {
      setActiveTab(tab);
    }
  }, [location.search]);

  const fetchBookings = async () => {
    try {
      const data = await apiFetch('/bookings/my');
      setBookings(data || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch ((status || '').toLowerCase()) {
      case 'confirmed':
      case 'ticketed': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'paid':
      case 'fulfillment_failed': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'pending_fulfillment':
      case 'pending':
      case 'authorised': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'failed':
      case 'cancelled':
      case 'payment_failed': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getDisplayStatus = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s === 'fulfillment_failed' || s === 'paid') return 'Paid';
    if (s === 'confirmed' || s === 'ticketed') return 'Confirmed';
    if (s === 'payment_failed') return 'Payment Failed';
    return s.replace('_', ' ');
  };

  const getServiceIcon = (type: string) => {
    switch (type) {
      case 'flight': return 'ri-plane-line';
      case 'hotel': return 'ri-hotel-line';
      case 'hajj': return 'ri-building-4-line';
      case 'umrah': return 'ri-star-line';
      default: return 'ri-suitcase-line';
    }
  };

  const formatCurrency = (amount: number) => {
    return '₦' + Number(amount).toLocaleString('en-US');
  };

  // Safe parsing of JSON details from PostgreSQL
  const parseDetails = (details: any) => {
    if (typeof details === 'string') {
      try { return JSON.parse(details); } catch { return {}; }
    }
    return details || {};
  };

  const getBookingDescription = (booking: Booking) => {
    const details = parseDetails(booking.booking_details);
    if (booking.service_type === 'flight') {
      const origin = details.origin || 'Unknown Origin';
      const dest = details.destination || 'Unknown Destination';
      return `${details.airline || 'Airline'} · ${origin} → ${dest}`;
    }
    if (booking.service_type === 'hotel') {
      return details.name || 'Hotel Stay';
    }
    return 'Travel Package';
  };

  const getPassengerCount = (booking: Booking) => {
    const details = parseDetails(booking.booking_details);
    if (details.passengers && Array.isArray(details.passengers)) {
      return `${details.passengers.length} Passenger${details.passengers.length > 1 ? 's' : ''}`;
    }
    return '';
  };

  const printReceipt = () => {
    window.print();
  };

  const handleDownloadTicket = async (booking: Booking) => {
    try {
      // @ts-ignore
      const { generateETicket } = await import('../../utils/pdfGenerator');
      await generateETicket(booking);
    } catch (error) {
      console.error('Error generating PDF ticket', error);
      alert('Failed to generate ticket PDF.');
    }
  };

  const isPaidOrConfirmed = (s: string) => ['confirmed', 'ticketed', 'paid', 'completed', 'fulfillment_failed'].includes((s || '').toLowerCase());
  const confirmedCount = bookings.filter(b => isPaidOrConfirmed(b.status)).length;
  const pendingCount = bookings.filter(b => ['pending', 'pending_fulfillment', 'authorised'].includes((b.status || '').toLowerCase())).length;
  const totalSpent = bookings.filter(b => isPaidOrConfirmed(b.status)).reduce((sum, b) => sum + Number(b.amount || 0), 0);

  const isFailedStatus = (s: string) => ['failed', 'cancelled', 'payment_failed'].includes((s || '').toLowerCase());
  const successfulBookings = bookings.filter(b => !isFailedStatus(b.status));
  const failedBookings = bookings.filter(b => isFailedStatus(b.status));

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      <div className="print:hidden">
        <Header />
      </div>

      {/* Premium Hero */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 pt-28 pb-16 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-logo-gold-400 to-logo-gold-600 flex items-center justify-center text-4xl font-bold text-navy-900 shadow-xl">
                {user?.displayName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full border-2 border-navy-900"></div>
            </div>
            
            <div className="text-center md:text-left flex-grow">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-1">
                Welcome back, {user?.displayName?.split(' ')[0] || 'Traveler'}
              </h1>
              <p className="text-gray-300 text-lg">{user?.email}</p>
            </div>

            <div className="flex gap-3 flex-wrap justify-center">
              <Link to="/general?tab=flights" className="bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 rounded-xl border border-white/20 transition-all flex items-center gap-2 text-sm font-medium">
                <i className="ri-plane-line"></i> Book Flight
              </Link>
              <Link to="/general?tab=hotels" className="bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 rounded-xl border border-white/20 transition-all flex items-center gap-2 text-sm font-medium">
                <i className="ri-hotel-line"></i> Book Hotel
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10 w-full print:hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <i className="ri-check-double-line text-emerald-600 text-2xl"></i>
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Successful Bookings</p>
                <p className="text-3xl font-bold text-navy-900">{confirmedCount}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <i className="ri-time-line text-amber-600 text-2xl"></i>
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Pending Processing</p>
                <p className="text-3xl font-bold text-navy-900">{pendingCount}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <i className="ri-error-warning-line text-red-600 text-2xl"></i>
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Failed Attempts</p>
                <p className="text-3xl font-bold text-red-600">{failedBookings.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-blue-600 text-2xl"></i>
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Spent</p>
                <p className="text-3xl font-bold text-navy-900">{formatCurrency(totalSpent)}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 w-full flex-grow pb-20 print:m-0 print:p-0">
        <div className="flex border-b border-gray-200 mb-6 md:mb-8 overflow-x-auto hide-scrollbar print:hidden">
          {(['overview', 'bookings', 'tickets', 'failed'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 md:px-6 py-3 text-sm font-semibold capitalize whitespace-nowrap transition-colors border-b-2 -mb-px flex items-center gap-2 ${
                activeTab === tab
                  ? 'text-navy-900 border-logo-gold-500'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              <span>{tab === 'failed' ? 'Failed Attempts' : tab === 'bookings' ? 'All Bookings' : tab === 'tickets' ? 'E-Tickets' : 'Overview'}</span>
              {tab === 'failed' && failedBookings.length > 0 && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-bold">
                  {failedBookings.length}
                </span>
              )}
              {tab === 'tickets' && successfulBookings.length > 0 && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700 font-bold">
                  {successfulBookings.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-8 animate-fade-in print:hidden">
            {/* Quick Actions */}
            <div>
              <h2 className="text-xl font-bold text-navy-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: 'ri-plane-fill', label: 'Search Flights', link: '/general?tab=flights', color: 'from-blue-500 to-blue-600' },
                  { icon: 'ri-hotel-fill', label: 'Find Hotels', link: '/general?tab=hotels', color: 'from-emerald-500 to-emerald-600' },
                  { icon: 'ri-suitcase-3-fill', label: 'Packages', link: '/packages', color: 'from-purple-500 to-purple-600' },
                  { icon: 'ri-customer-service-fill', label: 'Support', link: '/general', color: 'from-orange-500 to-orange-600' },
                ].map((action, i) => (
                  <Link key={i} to={action.link} className="group bg-white rounded-2xl p-5 shadow-md border border-gray-100 hover:-translate-y-1 transition-transform">
                    <div className={`w-12 h-12 bg-gradient-to-br ${action.color} rounded-xl flex items-center justify-center mb-3`}>
                      <i className={`${action.icon} text-white text-2xl`}></i>
                    </div>
                    <p className="font-semibold text-navy-900 text-sm">{action.label}</p>
                  </Link>
                ))}
              </div>
            </div>

            {/* Recent Bookings */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-navy-900">Recent Activity</h2>
                <button onClick={() => setActiveTab('bookings')} className="text-sm text-logo-gold-600 hover:text-logo-gold-700 font-medium">View All →</button>
              </div>
              {loading ? (
                <div className="text-center py-12 text-gray-400"><i className="ri-loader-4-line animate-spin text-3xl"></i></div>
              ) : successfulBookings.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-md p-12 text-center border border-gray-100">
                  <i className="ri-inbox-line text-5xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500 text-lg font-medium">No recent bookings</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {successfulBookings.slice(0, 5).map((booking) => (
                    <div key={booking.id} className="bg-white rounded-xl shadow-sm hover:shadow-md p-5 flex items-center justify-between border border-gray-100 transition-all">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-navy-50 rounded-lg flex items-center justify-center">
                          <i className={`${getServiceIcon(booking.service_type)} text-navy-600 text-xl`}></i>
                        </div>
                        <div>
                          <p className="font-bold text-navy-900">{getBookingDescription(booking)}</p>
                          <p className="text-sm text-gray-500 flex items-center gap-2">
                            <span>{new Date(booking.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            {getPassengerCount(booking) && (
                              <>
                                <span>•</span>
                                <span><i className="ri-user-line text-xs mr-1"></i>{getPassengerCount(booking)}</span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border capitalize ${getStatusColor(booking.status)}`}>{getDisplayStatus(booking.status)}</span>
                        <p className="font-bold text-navy-900 text-lg">{formatCurrency(booking.amount || 0)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* All Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="animate-fade-in print:hidden">
            {loading ? (
              <div className="text-center py-12 text-gray-400"><i className="ri-loader-4-line animate-spin text-3xl"></i></div>
            ) : successfulBookings.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-md p-12 text-center border border-gray-100">
                <i className="ri-inbox-line text-5xl text-gray-300 mb-4"></i>
                <p className="text-gray-500 text-lg font-medium">No bookings found</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-md overflow-x-auto border border-gray-100 hide-scrollbar">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 md:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Service</th>
                      <th className="px-4 md:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Details</th>
                      <th className="px-4 md:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="px-4 md:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="px-4 md:px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {successfulBookings.map(booking => (
                      <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <i className={`${getServiceIcon(booking.service_type)} text-navy-600 text-lg`}></i>
                            <span className="font-medium text-navy-900 capitalize">{booking.service_type}</span>
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-4 min-w-[200px]">
                          <p className="text-sm font-semibold text-navy-900">{getBookingDescription(booking)}</p>
                          <p className="text-xs text-gray-500">{getPassengerCount(booking)}</p>
                        </td>
                        <td className="px-4 md:px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{new Date(booking.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap"><span className={`px-3 py-1 rounded-full text-xs font-semibold border capitalize ${getStatusColor(booking.status)}`}>{getDisplayStatus(booking.status)}</span></td>
                        <td className="px-4 md:px-6 py-4 text-right font-bold text-navy-900 whitespace-nowrap">{formatCurrency(booking.amount || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Failed / Pending Tab */}
        {activeTab === 'failed' && (
          <div className="animate-fade-in print:hidden">
            {failedBookings.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-md p-12 text-center border border-gray-100">
                <i className="ri-shield-check-line text-5xl text-emerald-300 mb-4"></i>
                <p className="text-gray-500 text-lg font-medium">No failed transactions</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-md overflow-x-auto border border-gray-100 hide-scrollbar">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 md:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Service</th>
                      <th className="px-4 md:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Attempt Date</th>
                      <th className="px-4 md:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="px-4 md:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Reason / Note</th>
                      <th className="px-4 md:px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase">Amount</th>
                      <th className="px-4 md:px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {failedBookings.map(booking => {
                      const details = parseDetails(booking.booking_details);
                      const reason = typeof details.reason === 'string'
                        ? details.reason
                        : typeof details.paystack_error === 'string'
                          ? details.paystack_error
                          : 'Payment cancelled or card declined';
                      return (
                        <tr key={booking.id} className="hover:bg-red-50/60 transition-colors">
                          <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <i className={`${getServiceIcon(booking.service_type)} text-red-600 text-lg`}></i>
                              <span className="font-medium text-navy-900 capitalize">{booking.service_type}</span>
                            </div>
                          </td>
                          <td className="px-4 md:px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{new Date(booking.created_at).toLocaleString('en-GB')}</td>
                          <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold border uppercase ${getStatusColor(booking.status)}`}>
                              {booking.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 md:px-6 py-4 text-xs text-gray-500 max-w-xs truncate" title={reason}>
                            {reason}
                          </td>
                          <td className="px-4 md:px-6 py-4 text-right font-bold text-navy-900 whitespace-nowrap">{formatCurrency(booking.amount || 0)}</td>
                          <td className="px-4 md:px-6 py-4 text-right whitespace-nowrap">
                            <Link
                              to={booking.service_type === 'hotel' ? '/general?tab=hotels' : '/general?tab=flights'}
                              className="px-3 py-1.5 rounded-lg bg-navy-900 hover:bg-navy-800 text-white text-xs font-semibold transition-all inline-flex items-center gap-1 shadow-sm"
                            >
                              <i className="ri-refresh-line"></i> Retry
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tickets Tab (Printable) */}
        {activeTab === 'tickets' && (
          <div className="animate-fade-in">
            {successfulBookings.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-md p-12 text-center border border-gray-100 print:hidden">
                <i className="ri-ticket-2-line text-5xl text-gray-300 mb-4"></i>
                <p className="text-gray-500 text-lg font-medium">No tickets available</p>
                <p className="text-gray-400 mt-1">Once your booking is processed, your e-ticket will appear here</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print:block print:space-y-8">
                {successfulBookings.map((ticket) => {
                  const details = parseDetails(ticket.booking_details);
                  const pnr = details.pnr || details.letsfg_booking_id || ticket.id.slice(0, 8).toUpperCase();
                  
                  return (
                  <div key={ticket.id} className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow print:shadow-none print:border-2 print:border-gray-800 print:break-inside-avoid">
                    <div className="bg-gradient-to-r from-navy-900 to-navy-800 p-5 flex items-center justify-between print:bg-none print:bg-white print:border-b-2 print:border-gray-800">
                      <div className="flex items-center gap-3">
                        <i className={`${getServiceIcon(ticket.service_type)} text-logo-gold-400 text-3xl print:text-black`}></i>
                        <div>
                          <p className="text-white font-bold capitalize text-lg print:text-black">{ticket.service_type} E-Ticket</p>
                          <p className="text-gray-300 text-sm print:text-gray-600">Booking Ref: {pnr}</p>
                        </div>
                      </div>
                      <button onClick={() => handleDownloadTicket(ticket)} className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg text-sm border border-white/20 transition-all print:hidden flex items-center gap-1">
                        <i className="ri-download-line"></i> Download PDF
                      </button>
                    </div>
                    <div className="p-6">
                      <h3 className="font-bold text-xl text-navy-900 mb-4 border-b border-gray-100 pb-3">{getBookingDescription(ticket)}</h3>
                      
                      <div className="grid grid-cols-2 gap-y-4 gap-x-8 mb-6">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Issue Date</p>
                          <p className="font-semibold text-navy-900">{new Date(ticket.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Status</p>
                          <p className="font-semibold text-navy-900 capitalize flex items-center gap-1">
                            {ticket.status === 'confirmed' ? <i className="ri-checkbox-circle-fill text-emerald-500"></i> : <i className="ri-time-fill text-amber-500"></i>}
                            {ticket.status}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Passengers</p>
                          <p className="font-semibold text-navy-900">{getPassengerCount(ticket) || '1 Adult'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Amount Paid</p>
                          <p className="font-bold text-navy-900">{formatCurrency(ticket.amount || 0)}</p>
                        </div>
                      </div>

                      {details.passengers && details.passengers.length > 0 && (
                        <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-100">
                          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">Lead Passenger</p>
                          <p className="font-bold text-navy-900">{details.passengers[0].given_name || details.passengers[0].firstName} {details.passengers[0].family_name || details.passengers[0].lastName}</p>
                          <p className="text-sm text-gray-600">{details.contact_email}</p>
                        </div>
                      )}

                      <div className="border-t border-dashed border-gray-300 pt-4 mt-4 flex justify-between items-center">
                        <div className="flex flex-col">
                          <span className="text-[10px] text-gray-400 uppercase tracking-widest">Internal ID</span>
                          <span className="font-mono text-sm text-gray-500">{ticket.id.slice(0, 18)}</span>
                        </div>
                        <div className="text-right">
                          <img src={`https://api.qrserver.com/v1/create-qr-code/?size=60x60&data=${pnr}`} alt="QR Code" className="w-16 h-16 opacity-80" />
                        </div>
                      </div>
                    </div>
                  </div>
                )})}
              </div>
            )}
          </div>
        )}
      </section>

      <div className="print:hidden">
        <Footer />
      </div>

      {/* Print Styles */}
      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          body { background: white !important; }
          .glass { background: white !important; }
        }
      `}} />
    </div>
  );
}
